import './location-selector.scss'

import _ from 'lodash'
import { Observable, Subject, of, zip } from 'rxjs'
import { catchError, debounceTime, first, switchMap } from 'rxjs/operators'
import { defineComponent } from 'vue'
import { faBarcode, faCircleNotch } from '@fortawesome/free-solid-svg-icons'

import { SearchResponse, isSearchResponse, Location } from '@skinsoft/types'
import { locationSelectors } from '@skinsoft/selectors'
import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { BarcodeReader, BarcodeSubscriber } from '@skinsoft/ui'

const MONGO_ID_REGEX = /^[a-f\d]{24}$/i
const PAGE_SIZE = 15

export const LocationSelector = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sBarcodeReader: BarcodeReader,
        sBarcodeSubscriber: BarcodeSubscriber
    },
    props: [''],
    template: require('./location-selector.html'),
    data() {
        return {
            locations: [] as any[],
            searchText: null as any as string,

            value$: new Subject(),
            pending: true,
            changePagePending: false,
            currentPageIndex: 1,
            lastPageIndex: 1,
            isBarcodeReaderActive: false,
            displayPaths: false,

            // By default, no item is selected
            selectedItemIndex: -1,

            // Icons
            loadingIcon: faCircleNotch,
            barcodeIcon: faBarcode,
        }
    },
    mounted() {
        // Subscribe to text search
        this.subscribeTo(
            this.value$.pipe(
                debounceTime(750),
            ),
            searchText => {
                this.subscribeToLocations(true)
            }
        )
        // used to trigger watcher the first time
        this.searchText = ""
    },
    methods: {
        subscribeToLocations(resetPageIndex?: boolean) {
            this.unsubscribeFrom('locations-subscription')
            this.pending = true;
            this.displayPaths = false;

            const defaultSearch$ = this.$locationDataStore.searchAll(
                {
                    queryText: this.searchText,
                    pageSize: PAGE_SIZE,
                    page: this.currentPageIndex - 1,
                }
            )
            .pipe(
                first()
            )

            let search$: Observable<SearchResponse<Location>> = defaultSearch$

            if(this.searchText.match(MONGO_ID_REGEX)) {
                // if we search by id, the search will only return childrens of the location
                // so we need to fetch the location by id and add it to the result of the search
                search$ = this.$locationDataStore.fetchOne(this.searchText).pipe(
                    catchError(() => {
                        return defaultSearch$
                    }),
                    first(),
                    switchMap(response => {
                            if(response && !isSearchResponse(response)) {
                                return zip(
                                    of([response as Location]),
                                    defaultSearch$
                                )
                            }

                            return zip(of([]), defaultSearch$)
                    }),
                    switchMap(([resultFetchOne, resultDefaultSearch]) => {
                        const filteredFetchOneResult = resultFetchOne.filter(location => {
                            if(location.managementData?.archived) {
                                return false
                            }
                            return true
                        })

                        const resultComparator = (elem1, elem2) => {
                            if(elem1?.id && elem2?.id) {
                                return elem1.id === elem2.id
                            }

                            return false
                        }
                        const allData = _.uniqWith(filteredFetchOneResult.concat(resultDefaultSearch.allData), resultComparator)
                        const results = _.uniqWith(filteredFetchOneResult.concat(resultDefaultSearch.results), resultComparator)

                        return of ({
                            totalSize: allData.length > 0 ? allData.length : results.length,
                            allData: allData,
                            results: results
                        })
                    }),
                )
            }

            this.subscribeTo(
                search$,
                searchResponse => {
                    this.locations = searchResponse.allData;
                    Promise.all(this.locations.map(
                        async location => {
                            location.indexTitle = locationSelectors.title(location, this.getAppLocale())
                            location.pathIds = locationSelectors.pathSchema(location, this.getAppLocale())
                            if(location.pathIds && location.pathIds.value) {
                                location.pathIds = await this.$locationDataStore.resolvePath(location.pathIds.value, this.getAppLocale())
                            }
                        }
                    )).then(
                        () => { this.displayPaths = true }
                    )
                    this.pending = false;
                    this.changePagePending = false;
                    this.lastPageIndex = Math.floor(searchResponse.totalSize / PAGE_SIZE);

                    // To avoid extra empty page when totalSize is a multiple of pageSize
                    if (searchResponse.totalSize % PAGE_SIZE !== 0) {
                        this.lastPageIndex++;
                    }

                    if (resetPageIndex) {
                        this.currentPageIndex = 1;
                    }
                },
                'locations-subscription'
            )
        },
        incrementPageIndex() {
            const newPageIndex = this.currentPageIndex + 1;
            this.currentPageIndex = newPageIndex <= this.lastPageIndex ? newPageIndex : this.lastPageIndex;
        },
        decrementPageIndex() {
            const newPageIndex = this.currentPageIndex - 1;
            this.currentPageIndex = newPageIndex > 1 ? newPageIndex : 1;
        },
        goToLastPage() {
            this.currentPageIndex = this.lastPageIndex;
        },
        onItemSelected(location, index) {
            this.selectedItemIndex = index;
            this.$emit('location-changed', { location })
        },
        toggleBarcodeReader() {
            this.isBarcodeReaderActive = !this.isBarcodeReaderActive
        },
        onBarcodeReaderInput(value: string) {
            this.searchText = value;
            this.isBarcodeReaderActive = false;
        },
    },
    watch: {
        searchText() {
            this.value$.next(this.searchText)
        },
        currentPageIndex() {
            this.changePagePending = true;
            this.subscribeToLocations()
            this.selectedItemIndex = -1;
            this.$emit('location-changed', { location: null })
        },
    },
})
